import React from 'react';
import { NavLink, Stack, Text, Badge, rem } from '@mantine/core';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  IconDashboard,
  IconUsers,
  IconFileText,
  IconMail,
  IconChartBar,
  IconClock,
  IconTarget,
} from '@tabler/icons-react';
import { usePermissions } from '../../hooks/useAuth';

interface NavigationItem {
  label: string;
  path: string;
  icon: React.ComponentType<{ size?: string | number; style?: React.CSSProperties }>;
  badge?: string;
  children?: NavigationItem[];
  requiredPermission?: () => boolean;
}

interface NavigationSection {
  title?: string;
  items: NavigationItem[];
}

export const TeacherSidebar: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { canManageStudents, canViewReports } = usePermissions();

  // Navigation configuration for teacher portal - only show implemented routes
  const navigationSections: NavigationSection[] = [
    {
      title: 'Main',
      items: [
        {
          label: 'Dashboard',
          path: '/teacher/dashboard',
          icon: IconDashboard,
        },
      ],
    },
    {
      title: 'Class Management',
      items: [
        {
          label: 'Student Roster',
          path: '/teacher/students',
          icon: IconUsers,
          badge: '28', // Mock student count
          requiredPermission: canManageStudents,
        },
        {
          label: 'Attendance',
          path: '/teacher/attendance',
          icon: IconClock,
          requiredPermission: canManageStudents,
        },
      ],
    },
    {
      title: 'Activities',
      items: [
        {
          label: 'Verify Activities',
          path: '/teacher/verify',
          icon: IconTarget,
          badge: '5', // Mock pending verifications
        },
        {
          label: 'Student Progress',
          path: '/teacher/progress',
          icon: IconChartBar,
        },
      ],
    },
    {
      title: 'Reports',
      items: [
        {
          label: 'Daily Reports',
          path: '/teacher/reports',
          icon: IconFileText,
          badge: '3', // Mock reports to complete
          requiredPermission: canViewReports,
        },
      ],
    },
    {
      title: 'Communication',
      items: [
        {
          label: 'Messages',
          path: '/teacher/messages',
          icon: IconMail,
          badge: '4', // Mock unread messages
        },
      ],
    },
  ];

  const NavigationLink: React.FC<{ item: NavigationItem; depth?: number }> = ({ 
    item, 
    depth = 0 
  }) => {
    const isActive = location.pathname === item.path;
    const hasChildren = item.children && item.children.length > 0;

    const handleClick = () => {
      navigate(item.path);
    };

    return (
      <>
        <NavLink
          key={item.path}
          label={item.label}
          leftSection={<item.icon style={{ width: rem(16), height: rem(16) }} />}
          rightSection={
            item.badge && (
              <Badge size="xs" variant="filled" color="blue">
                {item.badge}
              </Badge>
            )
          }
          active={isActive && !hasChildren}
          onClick={handleClick}
          style={{
            paddingLeft: rem(12 + depth * 20),
            borderRadius: rem(6),
            marginBottom: rem(2),
          }}
        />

        {hasChildren && (
          <Stack gap={2} ml="md">
            {item.children!.map((child) => (
              <NavigationLink key={child.path} item={child} depth={depth + 1} />
            ))}
          </Stack>
        )}
      </>
    );
  };

  return (
    <Stack gap="lg" style={{ height: '100%' }}>
      {navigationSections.map((section, index) => (
        <div key={index}>
          {section.title && (
            <Text
              size="xs"
              fw={600}
              c="dimmed"
              tt="uppercase"
              mb="sm"
              style={{ letterSpacing: '0.05em' }}
            >
              {section.title}
            </Text>
          )}
          <Stack gap={2}>
            {section.items
              .filter((item) => !item.requiredPermission || item.requiredPermission())
              .map((item) => (
                <NavigationLink key={item.path} item={item} />
              ))}
          </Stack>
        </div>
      ))}

      {/* Bottom spacing for better visual balance */}
      <div style={{ flex: 1 }} />
    </Stack>
  );
};

export default TeacherSidebar;